// Copyright (c) 2018 Cigent Technology Inc. All Rights Reserved.

import {ChangeDetectorRef, Component, OnInit, QueryList, ViewChildren} from '@angular/core';
import {HeaderService} from '../../header.service';
import {BehaviorSubject, combineLatest, EMPTY, forkJoin, Observable} from 'rxjs';
import {MatTableDataSource} from '@angular/material/table';
import {switchMap} from 'rxjs/operators';
import {TranslateService} from '@ngx-translate/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {GroupsService, GroupType, IGroupSummary} from '../../../services/groups-service';
import {Router} from '@angular/router';
import {SelectionModel} from '@angular/cdk/collections';
import {EncryptionService} from '../../../services/encryption.service';
import {D3eService} from '../../../services/d3e-service';
import {MatSort} from '@angular/material/sort';
import {MatDialog} from '@angular/material/dialog';
import {AlertDialogComponent} from '../../dialogs/alert/alert.component';
import {UserService} from '../../../services/user-service';
import {OrgType} from '../../../types/group';
import { IUser } from '../../../types/user';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-groups',
    templateUrl: './groups.component.html',
    styleUrls: ['./groups.component.scss'],
    standalone: false
})
export class GroupsComponent implements OnInit {
  groupSummaries: MatTableDataSource<IGroupSummary> = new MatTableDataSource<IGroupSummary>();
  ready$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isMSP$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  selectedOrgId: string = '';
  searchTerm: string = '';
  columns: string[] = ['select', 'name', 'type', 'count'];
  now: Date = new Date();

  public readonly GroupType = GroupType;
  selection: SelectionModel<IGroupSummary> = new SelectionModel<IGroupSummary>(true);

  @ViewChildren(MatSort) sort = new QueryList<MatSort>();

  constructor(
    private readonly headerService: HeaderService,
    private readonly translate: TranslateService,
    private readonly groupsService: GroupsService,
    private readonly encryptionService: EncryptionService,
    private readonly d3eService: D3eService,
    private readonly dialog: MatDialog,
    private readonly router: Router,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly userService: UserService,
  ) {
    this.headerService.setOrgSelectorDefaultOption(translate.instant('SELECTOR_YOUR_GROUPS'));
    this.headerService.setTitle(translate.instant('TITLE_GROUPS'));
    this.headerService.setSubtitle('');
    this.headerService.setIcon('mdi-format-list-bulleted');
    this.headerService.setNavMenuText('');
    this.headerService.showUserFilter(true);

    combineLatest([
      this.headerService.orgSelectedChanged,
      this.userService.subscribeToUser(),
    ]).pipe(
      switchMap(([selectedOrgId, user]: [string, IUser]) => {
        this.selectedOrgId = selectedOrgId;
        if (user?.org_type.value === OrgType.MSP && selectedOrgId === 'All') {
          this.isMSP$.next(true);
          this.ready$.next(true);
          return EMPTY;
        }
        this.isMSP$.next(false);
        return this.getGroupSummaries(selectedOrgId);
      }),
      untilDestroyed(this)
    ).subscribe(this.setGroups.bind(this));

  }
  
  ngOnInit(): void {
  }

  getGroupSummaries(selectedOrgId): Observable<{groupSummaries: IGroupSummary[], defaultGroup: IGroupSummary}> {
    return forkJoin({
      groupSummaries: this.groupsService.getGroupSummaries(this.searchTerm, selectedOrgId),
      defaultGroup: this.groupsService.getDefaultGroup(selectedOrgId),
    });
  }

  setGroups({groupSummaries, defaultGroup}: {groupSummaries: IGroupSummary[], defaultGroup: IGroupSummary}) {
    this.selection.clear();
    groupSummaries.push(defaultGroup);
    this.groupSummaries.data = groupSummaries;
    this.groupSummaries.sortingDataAccessor = (groupSummary: IGroupSummary, sortHeaderId): string | number => {
      switch (sortHeaderId) {
        case 'name':
          return groupSummary.name.toLowerCase();
        case 'type':
          return groupSummary.type.toLowerCase();
        case 'count':
          return groupSummary.count;
        default:
          return groupSummary[sortHeaderId];
      }
    };
    this.groupSummaries.sort = this.sort.toArray()[0];
    this.changeDetectorRef.detectChanges();
    this.resort();
    this.now = new Date();
    this.ready$.next(true);
  }

  addButtonClick() {
    this.router.navigate(['/group-details']);
  }

  removeButtonClick() {
    const dialogRef = this.dialog.open(AlertDialogComponent, {
      width: '450px',
      data: {
        title: this.translate.instant('DELETE_GROUPS_TITLE'),
        content: this.translate.instant('DELETE_GROUPS_CONTENT'),
        rejectBtnName: this.translate.instant('NO'),
        acceptBtnName: this.translate.instant('YES'),
        showRejectBtn: true,
      },
    });

    dialogRef.afterClosed().pipe(
      untilDestroyed(this),
      switchMap(result => {
        if (result) {
          const requests = this.selection.selected.map(groupSummary => {
            const orgId = this.selectedOrgId === 'All' ? '' : this.selectedOrgId;
            if (groupSummary.type === GroupType.user) {
              return this.encryptionService.putKey(groupSummary.id, groupSummary.name, new Date(), orgId);
            } else {
              return this.d3eService.deleteDeviceSet(groupSummary.id.toString(), orgId);
            }
          });
          return forkJoin(requests);
        }
        return EMPTY;
      }),
      switchMap(() => this.getGroupSummaries(this.selectedOrgId))
    ).subscribe(this.setGroups.bind(this));
  }

  edit(groupSummary: IGroupSummary) {
    this.router.navigate(['group-details', groupSummary.type, groupSummary.id]);
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.groupSummaries.data.length;
    return numSelected === numRows;
  }

  masterToggle() {
    this.isAllSelected() ?
      this.selection.clear() :
      this.groupSummaries.data.forEach((groupSummary: IGroupSummary) => {
        this.selection.select(groupSummary);
      });
  }

  toggleAll() {
    this.masterToggle();
  }

  toggleRow(toggle: boolean, row: IGroupSummary) {
    if (toggle) {
      this.selection.select(row);
    } else {
      this.selection.deselect(row);
    }
  }

  resort() {
    const sort = this.sort.toArray()[0];
    sort.sortChange.emit({active: sort.active, direction: sort.direction});
  }
}
